import { indianMobileRegex, isMaskedValue } from '../../../utils/utils';
import {
    DIRECTOR_KNOWN_VALUES_KEY,
    EDIT_DETAILS_SUB_LABEL,
    EDIT_PROFILE_SUB_LABEL,
    KNOWN_DETAIL_NOUN,
    KNOWN_DETAIL_TAB_ORDER,
    MANUAL_SOURCE_ID,
    MASKED_SOURCE_HINT,
} from '../constants';
import type {
    AllKnownDetailsData,
    DirectorKnownValues,
    KnownDetailType,
    KnownEntity,
    KnownEntityType,
    KnownSourceGroup,
    KnownValue,
} from '../types';

/** Flattens groups in payload order; never sorts or de-duplicates. */
export const flattenValues = (groups?: KnownSourceGroup[]): KnownValue[] => (groups ?? []).flatMap((group) => group.values ?? []);

export const groupsForType = (entity: KnownEntity, type: KnownDetailType): KnownSourceGroup[] => entity.groups?.[type] ?? [];

export const deriveEntityCount = (entity: KnownEntity, type: KnownDetailType): number => flattenValues(groupsForType(entity, type)).length;

export const deriveTabCount = (data: AllKnownDetailsData | null, type: KnownDetailType): number =>
    (data?.entities ?? []).reduce((total, entity) => total + deriveEntityCount(entity, type), 0);

export const deriveTotalCount = (data: AllKnownDetailsData | null): number =>
    KNOWN_DETAIL_TAB_ORDER.reduce((total, type) => total + deriveTabCount(data, type), 0);

export const entitiesForType = (data: AllKnownDetailsData | null, type: KnownDetailType): KnownEntity[] =>
    (data?.entities ?? []).filter((entity) => deriveEntityCount(entity, type) > 0);

export const orgEntity = (data: AllKnownDetailsData | null): KnownEntity | undefined =>
    (data?.entities ?? []).find((entity) => entity.entityType === 'ORG');

export const findEntity = (data: AllKnownDetailsData | null, entityType: KnownEntityType, entityId?: string): KnownEntity | undefined =>
    entityId ? (data?.entities ?? []).find((entity) => entity.entityType === entityType && entity.entityId === entityId) : undefined;

export const toDirectorKnownValues = (entity?: KnownEntity): DirectorKnownValues | undefined =>
    entity && Object.fromEntries(KNOWN_DETAIL_TAB_ORDER.map((type) => [DIRECTOR_KNOWN_VALUES_KEY[type], groupsForType(entity, type)]));

/** Index of the server-flagged primary value, else the first. */
export const primaryIndexOf = (values: KnownValue[]): number =>
    Math.max(
        values.findIndex((value) => value.isPrimary),
        0,
    );

export function primaryValueOf<T extends KnownValue>(values: T[]): T | undefined {
    return values[primaryIndexOf(values)];
}

/** The server labels every group; the raw source id is the last resort. */
export const labelFor = (group: KnownSourceGroup): string => group.label ?? group.sourceId;

/** Server sub-label; only MANUAL's is derived, from the entity type. */
export const subLabelFor = (group: KnownSourceGroup, entityType: KnownEntityType): string | undefined => {
    if (group.subLabel) return group.subLabel;
    if (group.sourceId !== MANUAL_SOURCE_ID) return undefined;
    return entityType === 'ORG' ? EDIT_PROFILE_SUB_LABEL : EDIT_DETAILS_SUB_LABEL;
};

/** Shortest masked run per type; a type absent here is never treated as masked. */
const MASK_RUN_THRESHOLD: Partial<Record<KnownDetailType, number>> = { PHONE: 3, EMAIL: 3, ADDRESS: 6 };

const isMaskedFor = (value: string | undefined, type: KnownDetailType): boolean => {
    const minRun = MASK_RUN_THRESHOLD[type];
    return !!minRun && isMaskedValue(value, minRun);
};

export const maskedHintFor = (value: string | undefined, type: KnownDetailType): string | undefined =>
    isMaskedFor(value, type) ? MASKED_SOURCE_HINT : undefined;

const PHONE_SEPARATORS = /[\s()-]/g;

/** Country code or trunk prefix; `0091` before `0`, so the longest spelling wins. */
const INDIAN_PHONE_PREFIX = /^\+?(?:0{2}91|91|0)/;

/** A landline dialled through its area code groups its 6-8 digit subscriber number last; a mobile
 *  never leaves more than five digits after a separator, and separator-free the two are alike. */
const TRAILING_SUBSCRIBER_GROUP = /[\s()-]\d{6,8}$/;

/** Display only — never the value that is copied, compared or sent back. A prefix is dropped only
 *  when a whole mobile is left behind; a masked or area-code-grouped value renders as stored. */
export const displayValueFor = (value: string, type: KnownDetailType): string => {
    if (type !== 'PHONE' || isMaskedFor(value, type) || TRAILING_SUBSCRIBER_GROUP.test(value)) return value;
    const subscriberNumber = value.replace(PHONE_SEPARATORS, '').replace(INDIAN_PHONE_PREFIX, '');
    return indianMobileRegex.test(subscriberNumber) ? subscriberNumber : value;
};

/** Comparison key only — never rendered, copied or sent back. Equal keys are the same value.
 *  A phone is keyed off displayValueFor deliberately: what a surface shows and what it compares
 *  must not be able to drift apart again. */
const normalizedValueFor = (value: string, type: KnownDetailType): string => {
    if (type === 'PHONE') return displayValueFor(value, type).replace(PHONE_SEPARATORS, '');
    const trimmed = value.trim();
    return type === 'EMAIL' ? trimmed.toLowerCase() : trimmed;
};

/** The source as the drawer spells it: label, then the sub-label that qualifies it. */
const sourceLabelFor = (group: KnownSourceGroup, entityType: KnownEntityType): string => {
    const subLabel = subLabelFor(group, entityType);
    return `${labelFor(group)}${subLabel ? ` ${subLabel}` : ''}`;
};

export interface AttributedKnownValue extends KnownValue {
    source: string;
}

/** flattenValues, keeping each value's source label. Payload order; never de-duplicates. */
export const attributeValues = (groups: KnownSourceGroup[] | undefined, entityType: KnownEntityType): AttributedKnownValue[] =>
    (groups ?? []).flatMap((group) => {
        const source = sourceLabelFor(group, entityType);
        return (group.values ?? []).map((known) => ({ ...known, source }));
    });

/** Locates the value a surface is showing among the ones its sources reported. The two are raw
 *  spellings of the same thing — a prefix, a separator or a letter case apart — so both sides
 *  compare normalised. -1 when no source reported it, or the surface is showing nothing. */
export const indexOfDisplayedValue = (values: KnownValue[], displayedValue: string | undefined, type: KnownDetailType): number => {
    if (!displayedValue?.trim()) return -1;
    const shown = normalizedValueFor(displayedValue, type);
    return values.findIndex((entry) => normalizedValueFor(entry.value, type) === shown);
};

export const countLabel = (count: number, type: KnownDetailType): string => {
    const noun = KNOWN_DETAIL_NOUN[type];
    return `${count} ${count === 1 ? noun.singular : noun.plural}`;
};

export const padTabCount = (count: number): string => String(count).padStart(2, '0');
